/**
 * Менеджер состояния URL параметров для списков с фильтрацией и пагинацией
 * СИНХРОНИЗИРУЕТ существующие переменные компонента с URL
 *
 * Использование:
 * data() {
 *   return {
 *     search: '',
 *     page: 1,
 *     status: 'all'
 *   }
 * },
 * created() {
 *   createUrlStateManager(this, {
 *     search: { debounce: 300 },
 *     page: { type: 'number', default: 1 },
 *     status: { default: 'all' }
 *   });
 * }
 *
 * Template остается как обычно:
 * <input v-model="search" />
 */

export function createUrlStateManager(component, params, options = {}) {
  const config = {
    debounceDelay: 300,
    routeName: options.routeName || component.$route.name,
    additionalQuery: options.additionalQuery || {},
    onUpdate: options.onUpdate || null,
    ...options
  };

  const watchers = [];
  const timeouts = {};

  // Вспомогательная функция для работы с вложенными свойствами
  const getValue = (obj, path) => {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  };

  const setValue = (obj, path, value) => {
    const props = path.split('.');
    const lastProp = props.pop();
    const target = props.reduce((current, prop) => current[prop], obj);
    target[lastProp] = value;
  };

  // 1. Восстанавливаем значения из URL
  Object.keys(params).forEach(key => {
    const paramConfig = params[key] || {};
    // Используем urlKey если указан, иначе берем имя свойства (или последнюю часть для вложенных)
    const queryKey = paramConfig.urlKey || (key.includes('.') ? key.split('.').pop() : key);
    const urlValue = component.$route.query[queryKey];

    if (urlValue !== undefined) {
      // Парсим значение из URL
      let parsedValue = urlValue;

      if (paramConfig.type === 'number') {
        parsedValue = parseInt(urlValue) || paramConfig.default || 0;
      } else if (paramConfig.type === 'boolean') {
        parsedValue = urlValue === 'true' || urlValue === '1';
      }

      // Устанавливаем значение в компонент (поддержка вложенных свойств)
      if (key.includes('.')) {
        setValue(component, key, parsedValue);
      } else {
        component[key] = parsedValue;
      }
    } else if (paramConfig.default !== undefined) {
      // Используем default если нет в URL
      if (key.includes('.')) {
        setValue(component, key, paramConfig.default);
      } else {
        component[key] = paramConfig.default;
      }
    }
  });

  // 2. Функция обновления URL
  const updateURL = () => {
    const query = { ...config.additionalQuery };

    Object.keys(params).forEach(key => {
      const value = key.includes('.') ? getValue(component, key) : component[key];
      const paramConfig = params[key] || {};
      const defaultValue = paramConfig.default;
      // Используем urlKey если указан, иначе берем имя свойства
      const queryKey = paramConfig.urlKey || (key.includes('.') ? key.split('.').pop() : key);

      // Добавляем в URL только если не default
      if (value !== defaultValue && value !== '' && value !== null && value !== undefined) {
        // Для page/currentPage пропускаем 1
        if ((queryKey === 'page' || queryKey === 'currentPage') && value === 1) {
          return;
        }
        query[queryKey] = value;
      }
    });

    component.$router.replace({
      name: config.routeName,
      params: component.$route.params,
      query: query
    }).catch(err => {
      if (err.name !== 'NavigationDuplicated') {
        console.error(err);
      }
    });
  };

  // 3. Устанавливаем watchers на переменные СРАЗУ (не в nextTick!)
  Object.keys(params).forEach(key => {
    const paramConfig = params[key] || {};

    const watcher = component.$watch(key, (newValue, oldValue) => {
        // Обработка debounce для поиска
        if (paramConfig.debounce) {
          if (timeouts[key]) {
            clearTimeout(timeouts[key]);
          }

          timeouts[key] = setTimeout(() => {
            // Сбрасываем page/currentPage при изменении поиска
            const queryKey = key.includes('.') ? key.split('.').pop() : key;
            if (queryKey !== 'page' && queryKey !== 'currentPage') {
              if (component.page !== undefined) {
                component.page = 1;
              }
              if (component.pagination?.currentPage !== undefined) {
                component.pagination.currentPage = 1;
              }
            }
            updateURL();

            // Вызываем общий callback
            if (config.onUpdate) {
              config.onUpdate(key, newValue, oldValue);
            }

            // Вызываем специфичный callback для конкретного параметра
            const specificCallback = config[`on${key.charAt(0).toUpperCase() + key.slice(1)}Change`];
            if (specificCallback) {
              specificCallback(newValue, oldValue);
            }
          }, paramConfig.debounce);
        } else {
          // Без debounce - сразу обновляем
          updateURL();

          // Вызываем общий callback
          if (config.onUpdate) {
            config.onUpdate(key, newValue, oldValue);
          }

          // Вызываем специфичный callback для конкретного параметра
          const specificCallback = config[`on${key.charAt(0).toUpperCase() + key.slice(1)}Change`];
          if (specificCallback) {
            specificCallback(newValue, oldValue);
          }
        }
      });

      watchers.push(watcher);
    });

  // 4. Очистка при размонтировании компонента
  const originalBeforeUnmount = component.beforeUnmount;
  component.beforeUnmount = function() {
    // Очищаем watchers
    watchers.forEach(unwatch => unwatch());

    // Очищаем timeouts
    Object.values(timeouts).forEach(timeout => clearTimeout(timeout));

    // Вызываем оригинальный beforeUnmount если был
    if (originalBeforeUnmount) {
      originalBeforeUnmount.call(this);
    }
  };

  // 5. Возвращаем helper функцию для получения query параметров
  return {
    getQuery() {
      const query = {};
      Object.keys(params).forEach(key => {
        const value = key.includes('.') ? getValue(component, key) : component[key];
        const paramConfig = params[key] || {};
        // Используем urlKey если указан, иначе берем имя свойства
        const queryKey = paramConfig.urlKey || (key.includes('.') ? key.split('.').pop() : key);

        if (value !== paramConfig.default && value !== '' && value !== null && value !== undefined) {
          if ((queryKey === 'page' || queryKey === 'currentPage') && value === 1) return;
          query[queryKey] = value;
        }
      });
      return query;
    }
  };
}
